import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ADDRESS_CITY, ADDRESS_COUNTRY, ADDRESS_STATE } from '../constants/formFields';
import i18n from '../i18n';
import {
  useGetLocationsCountriesQuery,
  useLazyGeoLocationsCitiesQuery,
  useLazyGeoLocationsStatesQuery,
} from '../store/slices/geoLocations/apis/geoLocationsApi';
import createGeoLocationsOptions from '../utils/createGeoLocationsOptions';
import { showApiErrorToast } from '../utils/helpers';
import useDebouncedCallback from './useDebouncedCallback';

import type { FieldValues, Path, UseFormSetValue } from 'react-hook-form';
import type IOption from '../components/MappingModal/interfaces/IOption';
import type IGeoLocationsCitiesRequest from '../store/slices/geoLocations/interfaces/IGeoLocationsCitiesRequest';
import type { IUseGeoLocations } from './interfaces/IUseGeoLocations';
import type { TAsyncSelectResolve } from './interfaces/TAsyncSelectResolve';

const useGeoLocations = <T extends FieldValues>({ watch, setValue }: IUseGeoLocations<T>) => {
  const { recordId = '' } = useParams();
  const [isStatesEditable, setIsStatesEditable] = useState(!recordId);
  const [isCitiesEditable, setIsCitiesEditable] = useState(!recordId);
  const [geoLocationsStates] = useLazyGeoLocationsStatesQuery();
  const [geoLocationsCities] = useLazyGeoLocationsCitiesQuery();
  const { data: locationsCountries } = useGetLocationsCountriesQuery();

  const countryField = ADDRESS_COUNTRY as Path<T>;
  const stateField = ADDRESS_STATE as Path<T>;
  const cityField = ADDRESS_CITY as Path<T>;
  const emptyOption = null as Parameters<UseFormSetValue<T>>[1];

  // Convert countries data to options format
  const allCountriesOptions = useMemo(() => {
    if (!locationsCountries?.data) return [];
    return locationsCountries.data.map((country) => ({
      value: country.iso2,
      label: country.name,
    }));
  }, [locationsCountries?.data]);

  // Client-side filtering for countries
  const filterCountries = (inputValue: string): IOption[] => {
    if (!inputValue) return allCountriesOptions;

    const lowercasedInput = inputValue.toLowerCase();
    return allCountriesOptions.filter(
      (option) => option.label.toLowerCase().includes(lowercasedInput),
    );
  };

  // Build the cities request from the selected region. A freshly-selected region carries a
  // Google place id in `value` (distinct from its display name); a region reloaded from a
  // saved record only has its name, stored as both `value` and `label`. Send the place id
  // when we have a real one, otherwise fall back to the name — the BE prioritises the id.
  const buildCitiesParams = (inputValue: string): IGeoLocationsCitiesRequest => {
    const country = watch(countryField)?.value;
    const state = watch(stateField);
    const hasPlaceId = Boolean(state?.value) && state?.value !== state?.label;

    return hasPlaceId
      ? { keyword: inputValue, country, placeId: state?.value }
      : { keyword: inputValue, country, name: state?.label ?? state?.value };
  };

  // The country can be cleared while a search waits out the debounce, so re-read it when the
  // timer fires. Without a country the request is meaningless — resolve empty and keep the
  // promise settled, otherwise the select stays stuck on its loading message.
  const geoLocationsStatesDebounced = useDebouncedCallback(
    (inputValue: string, resolve: TAsyncSelectResolve) => {
      const country = watch(countryField)?.value;
      if (!country) {
        resolve([]);
        return;
      }

      geoLocationsStates({ keyword: inputValue, country }).unwrap().then((data) => {
        const statesOptions: IOption[] = createGeoLocationsOptions({ data: data?.data || [], isState: true });
        resolve(statesOptions);
      }).catch((error) => {
        showApiErrorToast(error, i18n.t('common.unknownError'));
        resolve([]);
      });
    },
  );

  const geoLocationsCitiesDebounced = useDebouncedCallback(
    (inputValue: string, resolve: TAsyncSelectResolve) => {
      if (!watch(countryField)?.value) {
        resolve([]);
        return;
      }

      geoLocationsCities(buildCitiesParams(inputValue)).unwrap().then((data) => {
        const statesOptions: IOption[] = createGeoLocationsOptions({ data: data?.data || [] });
        resolve(statesOptions);
      }).catch((error) => {
        showApiErrorToast(error, i18n.t('common.unknownError'));
        resolve([]);
      });
    },
  );

  // Client-side search for countries
  const onFindCountries = (inputValue: string): Promise<IOption[]> => new Promise((resolve) => {
    const filteredCountries = filterCountries(inputValue);
    resolve(filteredCountries);
  });

  // react-select only calls a loader once the input is non-empty: it fires loadOptions on mount
  // for `defaultOptions === true` (which these selects never pass), and returns early from
  // onInputChange when the input clears.
  const onFindStates = (inputValue: string): Promise<IOption[]> => new Promise((resolve) => {
    geoLocationsStatesDebounced(inputValue, resolve);
  });

  const onFindCities = (inputValue: string): Promise<IOption[]> => new Promise((resolve) => {
    geoLocationsCitiesDebounced(inputValue, resolve);
  });
  const isStatesDisabled = !watch(countryField)?.value || !isStatesEditable;
  const isCitiesDisabled = !watch(stateField)?.value || isStatesDisabled || !isCitiesEditable;

  const onChangeCountry = () => {
    setValue(stateField, emptyOption);
    setValue(cityField, emptyOption);
    setIsStatesEditable(true);
    setIsCitiesEditable(true);
  };

  const onChangeState = () => {
    setValue(cityField, emptyOption);
    setIsCitiesEditable(true);
  };

  return {
    onFindCountries,
    onFindStates,
    onFindCities,
    isStatesDisabled,
    isCitiesDisabled,
    onChangeCountry,
    onChangeState,
    defaultCountries: allCountriesOptions,
  };
};

export default useGeoLocations;
